import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Alert, 
  List, 
  Typography, 
  Space, 
  Button, 
  Select, 
  Timeline, 
  Tooltip 
} from 'antd';
import {
  BankOutlined,
  EnvironmentOutlined,
  MobileOutlined,
  FileImageOutlined,
  WifiOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSimpleStore } from '../stores/simpleStore';
import { useCompanyScope, useAuth } from '../hooks/usePermissions';
import { colors, spacing, shadows } from '../styles/theme';
import { PageLayout, StatusIndicator, LoadingSpinner, ErrorBoundary } from './common';
import type { StatusType } from './common';
import OperatorDashboard from './OperatorDashboard';
import { getApiUrl } from '../utils/api';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const { Title, Text } = Typography;

interface DashboardStats {
  totalCompanies: number;
  totalRoutes: number;
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  deviceUptime: number;
  totalMedia: number;
  totalPlaylists: number;
}

interface SystemStatus {
  database: { status: string; message: string };
  busNavServer: { status: string; data?: any; error?: string };
  scheduler: { status: string; activeJobs: any[]; lastWeatherUpdate?: string };
  system: {
    uptime: number;
    memory: { used: number; total: number };
    disk: { used: number; available: number; percent: number };
  };
}


// 메모이제이션된 통계 카드 컴포넌트
const StatCard = React.memo<{
  title: string;
  value: number | string;
  suffix?: string;
  prefix?: React.ReactNode;
  color?: string;
  progress?: number;
  trend?: 'up' | 'down';
  icon?: React.ReactNode;
}>(({ title, value, suffix, prefix, color = colors.primary[500], progress, icon }) => (
  <Card
    size="small"
    style={{
      height: '100%',
      boxShadow: shadows.sm,
    }}
    styles={{
      body: { padding: spacing[4] }
    }}
  >
    <Statistic
      title={
        <Space>
          {icon}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {title}
          </Text>
        </Space>
      }
      value={value}
      suffix={suffix}
      prefix={prefix}
      valueStyle={{ color, fontSize: 24, fontWeight: 600 }}
    />
    {typeof progress === 'number' && (
      <Progress 
        percent={progress} 
        showInfo={false} 
        strokeColor={color}
        size="small"
        style={{ marginTop: spacing[2] }}
      />
    )}
  </Card>
));

// 메모이제이션된 시스템 상태 카드
const SystemStatusCard = React.memo<{ status: SystemStatus | null }>(({ status }) => {
  const getStatusType = (statusValue: string | undefined): StatusType => {
    if (!statusValue) return 'error';
    switch (statusValue.toLowerCase()) {
      case 'healthy':
      case 'online':
      case 'active':
        return 'success';
      case 'warning':
      case 'degraded':
        return 'warning';
      case 'error':
      case 'offline':
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!status) return null;

  return (
    <Card 
      title="시스템 상태" 
      size="small"
      style={{ boxShadow: shadows.sm }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>데이터베이스</Text>
          <StatusIndicator 
            status={getStatusType(status?.database?.status)}
            text={status?.database?.status || 'Unknown'}
            size="small"
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>버스 서버</Text>
          <StatusIndicator 
            status={getStatusType(status?.busNavServer?.status)}
            text={status?.busNavServer?.status || 'Unknown'}
            size="small"
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>스케줄러</Text>
          <StatusIndicator 
            status={getStatusType(status?.scheduler?.status)}
            text={status?.scheduler?.status || 'Unknown'}
            size="small"
          />
        </div>
        {status?.system && (
          <>
            <div style={{ marginTop: spacing[3] }}>
              <Text type="secondary" style={{ fontSize: 12 }}>메모리 사용량</Text>
              <Progress 
                percent={status?.system?.memory?.used && status?.system?.memory?.total ? 
                  Math.round((status.system.memory.used / status.system.memory.total) * 100) : 0}
                size="small"
                format={(percent) => `${percent}%`}
              />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>디스크 사용량</Text>
              <Progress 
                percent={status?.system?.disk?.percent || 0}
                size="small"
                status={(status?.system?.disk?.percent || 0) > 80 ? 'exception' : 'normal'}
              />
            </div>
          </>
        )}
      </Space>
    </Card>
  );
});

export default function RealDashboardImproved() {
  const { user } = useSimpleStore();
  const { isMobile } = useResponsive();
  const { scope, getCompanyFilter, currentCompanyId } = useCompanyScope();
  const { isOperator } = useAuth();
  
  // 운수사 운영자인 경우 전용 대시보드 표시
  if (isOperator && user?.company_id) {
    return <OperatorDashboard />;
  }
  
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [companiesSummary, setCompaniesSummary] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // 데이터 로드 함수 메모이제이션
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyId = currentCompanyId || selectedCompany;

      // 병렬로 데이터 로드 (fetchWithAuth 사용 - 401 에러 시 자동 로그아웃)
      const requests = [
        fetchWithAuth(getApiUrl(`/api/dashboard/overview${companyId ? `?company_id=${companyId}` : ''}`)),
        fetchWithAuth(getApiUrl('/api/dashboard/system-status'))
      ];

      // 관리자인 경우 운수사 통계와 요약 추가
      if (user?.role === 'admin') {
        requests.push(
          fetchWithAuth(getApiUrl('/api/companies/stats')),
          fetchWithAuth(getApiUrl('/api/dashboard/companies/summary'))
        );
      }

      const responses = await Promise.all(requests);
      let overviewRes, systemRes, companyStatsRes, companiesRes;

      if (user?.role === 'admin') {
        [overviewRes, systemRes, companyStatsRes, companiesRes] = responses;
      } else {
        [overviewRes, systemRes] = responses;
      }

      // 응답 처리
      if (overviewRes.ok) {
        const overviewData = await overviewRes.json();
        if (overviewData.success) {
          let dashboardStats = overviewData.data.stats || overviewData.data;

          // 관리자인 경우 companies stats API로 보완하고 정확한 데이터 적용
          if (user?.role === 'admin' && companyStatsRes?.ok) {
            const companyStatsData = await companyStatsRes.json();
            if (companyStatsData.success) {
              dashboardStats = {
                ...dashboardStats,
                // 실제 활성 운수사 수 사용 (API에서 제공하는 정확한 값)
                totalCompanies: companyStatsData.data.activeCompanies || companyStatsData.data.totalCompanies || dashboardStats.totalCompanies,
                // 실제 노선 수 사용
                totalRoutes: companyStatsData.data.totalRoutes || dashboardStats.totalRoutes,
                // 실제 온라인 단말기 수 사용 (0대라면 0으로 정확히 표시)
                onlineDevices: dashboardStats.onlineDevices,
                // 실제 미디어 수 사용 (overview API에서 오는 정확한 값)
                totalMedia: dashboardStats.totalMedia
              };
            }
          }

          setStats(dashboardStats);
        }
      }

      if (systemRes.ok) {
        const systemData = await systemRes.json();
        if (systemData.success) {
          setSystemStatus(systemData.data);
        }
      }

      if (companiesRes && companiesRes.ok) {
        const companiesData = await companiesRes.json();
        if (companiesData.success) {
          setCompaniesSummary(companiesData.data || []);
        }
      }

      setLastUpdate(new Date());
    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
      setError('대시보드 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentCompanyId, selectedCompany, user?.role]);

  // 초기 로드 및 주기적 갱신
  useEffect(() => {
    loadDashboardData();
    
    const interval = setInterval(loadDashboardData, 30000); // 30초마다 갱신
    
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  // 통계 카드 데이터 메모이제이션
  const statCards = useMemo(() => {
    if (!stats) return [];
    
    return [
      {
        key: 'companies',
        title: '운수사',
        value: stats.totalCompanies,
        icon: <BankOutlined />,
        color: colors.primary[500],
      },
      {
        key: 'routes',
        title: '노선',
        value: stats.totalRoutes,
        icon: <EnvironmentOutlined />,
        color: colors.success[500],
      },
      {
        key: 'devices',
        title: '단말기',
        value: stats.totalDevices,
        suffix: '대',
        icon: <MobileOutlined />,
        color: colors.info[500],
      },
      {
        key: 'online',
        title: '온라인 단말기',
        value: stats.onlineDevices,
        suffix: `/ ${stats.totalDevices}`,
        icon: <WifiOutlined />,
        color: colors.success[500],
        progress: stats.totalDevices > 0 ? Math.round((stats.onlineDevices / stats.totalDevices) * 100) : 0,
      },
      {
        key: 'media',
        title: '미디어',
        value: stats.totalMedia,
        suffix: '개',
        icon: <FileImageOutlined />,
        color: colors.warning[500],
      },
    ];
  }, [stats]);

  // 새로고침 핸들러
  const handleRefresh = useCallback(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (loading && !stats) {
    return <LoadingSpinner message="대시보드 데이터를 불러오는 중..." fullScreen />;
  }

  return (
    <ErrorBoundary>
      <PageLayout
        title="관리자 대시보드"
        subtitle="시스템 전체 현황을 한눈에 확인하세요"
        actions={[
          {
            key: 'refresh',
            label: '새로고침',
            icon: <ReloadOutlined />,
            onClick: handleRefresh,
            loading: loading,
          }
        ]}
        extra={
          lastUpdate && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              마지막 업데이트: {lastUpdate.toLocaleTimeString()}
            </Text>
          )
        }
        error={error}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 통계 카드 */}
          <Row gutter={[16, 16]}>
            {statCards.map((card) => {
              const { key, ...cardProps } = card;
              return (
                <Col xs={24} sm={12} lg={8} xl={4} xxl={4} key={key}>
                  <StatCard {...cardProps} />
                </Col>
              );
            })}
          </Row>

          <Row gutter={[16, 16]}>
            {/* 시스템 상태 */}
            <Col xs={24} lg={12}>
              <SystemStatusCard status={systemStatus} />
            </Col>

            {/* 운수사 요약 (admin만) */}
            {user?.role === 'admin' && companiesSummary.length > 0 && (
              <Col xs={24} lg={12}>
                <Card 
                  title="운수사 요약" 
                  size="small"
                  style={{ boxShadow: shadows.sm }}
                >
                  <List
                    size="small"
                    dataSource={companiesSummary.slice(0, 5)}
                    renderItem={(company) => (
                      <List.Item>
                        <List.Item.Meta
                          title={
                            <Text style={{ fontSize: 13 }}>
                              {company.name}
                            </Text>
                          }
                          description={
                            <Space size="small">
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                노선 {company.routes}개
                              </Text>
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                단말기 {company.devices}대
                              </Text>
                            </Space>
                          }
                        />
                        <StatusIndicator
                          status={company.online_devices > 0 ? 'success' : 'error'}
                          text={`${company.online_devices}/${company.devices}`}
                          size="small"
                        />
                      </List.Item>
                    )}
                    locale={{ emptyText: '운수사 정보가 없습니다.' }}
                  />
                </Card>
              </Col>
            )}
          </Row>
        </Space>
      </PageLayout>
    </ErrorBoundary>
  );
}